/**
 * Change detection on the umpire-desk crop.
 * Keep in sync with Backend/src/utils/championshipScoreChange.pure.js
 *
 * A still board is an area-averaged 32×24 thumbnail. A localized digit flip
 * is sent to OCR on the frame it appears. A hand-sized change waits until
 * the crop settles, then reads anyway so a point cannot stay stuck.
 */

export const FINGERPRINT_W = 32;
export const FINGERPRINT_H = 24;
const CELL = 4;
const SETTLE_MAD = 8;
const CHANGE_MAD = 4.5;
const CELL_DELTA = 10;
const MIN_CHANGED_CELLS = 2;
const LIGHT_SHIFT = 16;
const LIGHT_STD = 8;
/** Full-crop motion (hand, card mid-flip). Digit flips stay under this. */
const HAND_MAD = 42;
const HAND_CELLS = 18;
export const HEARTBEAT_MS = 120_000;
/** Live scorecam sample period. Digit flips are read on the sample that sees them. */
export const UMPIRE_SCORE_POLL_MS = 400;

function luma(r, g, b) {
  return (77 * r + 150 * g + 29 * b) >> 8;
}

export function fingerprintFromRgba(rgba, width, height) {
  const out = new Uint8Array(FINGERPRINT_W * FINGERPRINT_H);
  if (!rgba || width < 2 || height < 2) return out;
  for (let y = 0; y < FINGERPRINT_H; y += 1) {
    const y0 = Math.floor((y * height) / FINGERPRINT_H);
    const y1 = Math.max(y0 + 1, Math.floor(((y + 1) * height) / FINGERPRINT_H));
    for (let x = 0; x < FINGERPRINT_W; x += 1) {
      const x0 = Math.floor((x * width) / FINGERPRINT_W);
      const x1 = Math.max(x0 + 1, Math.floor(((x + 1) * width) / FINGERPRINT_W));
      let sum = 0;
      let count = 0;
      for (let sy = y0; sy < y1; sy += 1) {
        const row = sy * width;
        for (let sx = x0; sx < x1; sx += 1) {
          const i = (row + sx) * 4;
          sum += luma(rgba[i], rgba[i + 1], rgba[i + 2]);
          count += 1;
        }
      }
      out[y * FINGERPRINT_W + x] = count ? (sum / count) | 0 : 0;
    }
  }
  return out;
}

/** Compact metrics for scorecam HUD / console debug. */
export function summarizeCompare(cmp) {
  if (!cmp) return "";
  const mad = Number(cmp.mad);
  const cells = Number(cmp.cellsChanged);
  const shift = Number(cmp.globalMeanShift);
  const std = Number(cmp.cellDeltaStd);
  const parts = [];
  if (Number.isFinite(mad)) parts.push(`mad=${mad.toFixed(1)}`);
  if (Number.isFinite(cells)) parts.push(`cells=${cells}`);
  if (Number.isFinite(shift)) parts.push(`shift=${shift.toFixed(1)}`);
  if (Number.isFinite(std)) parts.push(`std=${std.toFixed(1)}`);
  return parts.join(" ");
}

export function compareFingerprints(a, b) {
  const n = FINGERPRINT_W * FINGERPRINT_H;
  if (!a || !b || a.length < n || b.length < n) {
    return { mad: 255, cellsChanged: 99, globalMeanShift: 255, cellDeltaStd: 0 };
  }
  let sad = 0;
  let sumA = 0;
  let sumB = 0;
  const cols = FINGERPRINT_W / CELL;
  const rows = FINGERPRINT_H / CELL;
  const cellDeltas = [];
  let cellsChanged = 0;
  for (let cy = 0; cy < rows; cy += 1) {
    for (let cx = 0; cx < cols; cx += 1) {
      let cA = 0;
      let cB = 0;
      let count = 0;
      for (let iy = 0; iy < CELL; iy += 1) {
        for (let ix = 0; ix < CELL; ix += 1) {
          const idx = (cy * CELL + iy) * FINGERPRINT_W + (cx * CELL + ix);
          const va = a[idx];
          const vb = b[idx];
          sad += Math.abs(va - vb);
          sumA += va;
          sumB += vb;
          cA += va;
          cB += vb;
          count += 1;
        }
      }
      const delta = Math.abs(cA / count - cB / count);
      cellDeltas.push(delta);
      if (delta >= CELL_DELTA) cellsChanged += 1;
    }
  }
  const mad = sad / n;
  const globalMeanShift = Math.abs(sumA / n - sumB / n);
  const meanDelta = cellDeltas.reduce((s, v) => s + v, 0) / cellDeltas.length;
  const cellDeltaStd = Math.sqrt(
    cellDeltas.reduce((s, v) => s + (v - meanDelta) ** 2, 0) / cellDeltas.length
  );
  return { mad, cellsChanged, globalMeanShift, cellDeltaStd };
}

function looksLikeLighting(cmp) {
  return cmp.globalMeanShift >= LIGHT_SHIFT && cmp.cellDeltaStd < LIGHT_STD;
}

function looksLikeDigitFlip(cmp) {
  if (looksLikeLighting(cmp)) return false;
  return cmp.cellsChanged >= MIN_CHANGED_CELLS || cmp.mad >= CHANGE_MAD;
}

function looksLikeHand(cmp) {
  return cmp.mad >= HAND_MAD || cmp.cellsChanged >= HAND_CELLS;
}

export function shouldReadScore({
  committed,
  previous,
  current,
  lastReadAt = 0,
  now = Date.now(),
  changeSeenAt = null,
  motionForceMs = 600,
}) {
  if (!current) return { read: false, reason: "empty" };
  if (!committed) return { read: true, reason: "baseline" };
  if (now - Number(lastReadAt || 0) >= HEARTBEAT_MS) {
    return { read: true, reason: "heartbeat" };
  }
  const vsPrev = previous ? compareFingerprints(previous, current) : null;
  const vsCommit = compareFingerprints(committed, current);
  const settled = !vsPrev || vsPrev.mad <= SETTLE_MAD;
  const digit = looksLikeDigitFlip(vsCommit);
  if (!digit) {
    if (!settled) return { read: false, reason: "in_motion", vsPrev, vsCommit };
    return { read: false, reason: "same_board", vsPrev, vsCommit };
  }
  // A finger over the board is large. Wait until it clears, but not forever.
  if (looksLikeHand(vsCommit) && !settled) {
    const age =
      changeSeenAt == null || !Number.isFinite(Number(changeSeenAt))
        ? 0
        : now - Number(changeSeenAt);
    if (age < motionForceMs) {
      return { read: false, reason: "in_motion", vsPrev, vsCommit };
    }
  }
  return { read: true, reason: "board_changed", vsPrev, vsCommit };
}

/**
 * Lag from the digit flip to this read.
 * Localized flips are stamped on the sample that sees them (~half a poll).
 * A hand in the crop adds the settle wait. Gemini time is not included.
 */
export function estimateScoreFlipLagMs({
  now = Date.now(),
  changeSeenAt = null,
  pollMs = UMPIRE_SCORE_POLL_MS,
} = {}) {
  const poll = Math.max(1, Number(pollMs) || UMPIRE_SCORE_POLL_MS);
  const seen = Number(changeSeenAt);
  if (Number.isFinite(seen) && seen > 0) {
    return Math.max(0, now - seen + poll / 2);
  }
  return poll * 1.5;
}

export function createBoardChangeGate({ pollMs = UMPIRE_SCORE_POLL_MS } = {}) {
  const poll = Math.max(1, Number(pollMs) || UMPIRE_SCORE_POLL_MS);
  const motionForceMs = Math.round(poll * 1.5);
  return {
    committed: null,
    previous: null,
    lastReadAt: 0,
    /** Wall-clock when the crop first diverged from the committed board. */
    changeSeenAt: null,
    rollback: null,
    readSeq: 0,
    observe(fingerprint, now = Date.now()) {
      const decision = shouldReadScore({
        committed: this.committed,
        previous: this.previous,
        current: fingerprint,
        lastReadAt: this.lastReadAt,
        now,
        changeSeenAt: this.changeSeenAt,
        motionForceMs,
      });
      if (
        this.committed &&
        (decision.reason === "in_motion" || decision.reason === "board_changed")
      ) {
        if (this.changeSeenAt == null) this.changeSeenAt = now;
      } else if (decision.reason === "same_board" || decision.reason === "baseline") {
        this.changeSeenAt = null;
      }
      const changeSeenAt = this.changeSeenAt;
      const detectLagMs = estimateScoreFlipLagMs({
        now,
        changeSeenAt: decision.reason === "board_changed" ? changeSeenAt : null,
        pollMs: poll,
      });
      this.previous = fingerprint;
      let readSeq = 0;
      if (decision.read) {
        this.readSeq += 1;
        readSeq = this.readSeq;
        this.rollback = {
          seq: readSeq,
          committed: this.committed,
          lastReadAt: this.lastReadAt,
        };
        this.committed = fingerprint;
        this.lastReadAt = now;
        this.changeSeenAt = null;
      }
      return {
        ...decision,
        change_seen_at: changeSeenAt,
        detect_lag_ms: decision.read ? detectLagMs : 0,
        read_seq: readSeq,
      };
    },
    /** Server dropped this read (rate limit / transport). Sample it again. */
    rejectRead(seq) {
      if (!this.rollback || this.rollback.seq !== seq) return false;
      this.committed = this.rollback.committed;
      this.lastReadAt = this.rollback.lastReadAt;
      this.rollback = null;
      return true;
    },
    reset() {
      this.committed = null;
      this.previous = null;
      this.lastReadAt = 0;
      this.changeSeenAt = null;
      this.rollback = null;
      this.readSeq = 0;
    },
  };
}
